import { memo } from 'react';
import { motion, AnimatePresence } from 'motion/react';

import { cn } from '@/shared/shadcn/lib/utils';
import { ChevronDownIcon } from 'lucide-react';
import { SIDEBAR_WIDTH } from '@/shared/constants';
import { selectIsSidebarWide, useSidebarStore } from '@/shared/components/layout/_useSidebarStore';
import { hasActiveDescendant, isItemActive, matchesActivePathPrefix } from '@/shared/components/layout/_activePath';

import type { NavigateFunction } from 'react-router-dom';
import type { SidebarMenuItem } from '@/shared/components/layout/types';

interface SidebarNavItemProps {
  menuItem: SidebarMenuItem;
  /**
   * 현재 경로. 행마다 useLocation()을 부르면 쿼리 파라미터만 바뀌어도 트리 전체가 리렌더된다
   * (목록 화면의 필터·페이지네이션이 전부 쿼리 파라미터라 매우 잦다). 상위에서 한 번만 구독해 내려준다.
   */
  pathname: string;
  /**
   * 상위에서 내려받는 navigate.
   * 데이터 라우터에서는 RouteContext 값이 라우터 상태가 바뀔 때마다 새로 만들어지므로,
   * 행마다 useNavigate()를 부르면 그 자체로 구독이 생겨 memo가 무력화된다.
   */
  navigate: NavigateFunction;
  /** 중첩 깊이 (0 = 최상위). 1차 들여쓰기는 크게, 2차부터는 작게 주기 위해 필요 */
  depth?: number;
  /** 조상들의 id를 이어붙인 펼침 키 — 다른 depth의 동일 id끼리 상태가 섞이지 않게 함 */
  parentKey?: string;
  className?: string;
}

const COLLAPSED_RAIL_REM = parseFloat(SIDEBAR_WIDTH.COLLAPSED);

/**
 * 아이콘 컬럼 폭
 * - 접힘: 버튼(mx-1) 안에서 아이콘이 레일 정중앙에 오도록 레일 폭에 맞춘다
 * - 펼침: 아이콘과 제목 사이 간격을 좁힌다
 */
const ICON_COLUMN_WIDTH = {
  collapsed: `${COLLAPSED_RAIL_REM - 0.75}rem`,
  expanded: `${COLLAPSED_RAIL_REM - 2}rem`,
};

/** 접힘↔펼침 전환 시간 — 사이드바 패널 확장과 어긋나 보이지 않도록 한곳에서 관리 */
const TRANSITION = {
  /** 폭·위치처럼 레이아웃이 함께 움직이는 요소 */
  layout: { duration: 0.2 },
  /** 텍스트 페이드처럼 살짝 먼저 끝나야 자연스러운 요소 */
  fade: { duration: 0.15 },
};

const SidebarNavItem = memo(function SidebarNavItem({ menuItem, pathname, navigate, depth = 0, parentKey, className }: SidebarNavItemProps) {
  const isChildItem = depth > 0;
  const hasIcon = !!menuItem.Icon;
  const expandKey = parentKey ? `${parentKey}/${menuItem.id}` : menuItem.id;

  // store 직접 구독 — 부모 리렌더에서 격리
  const isSidebarWide = useSidebarStore(selectIsSidebarWide);
  const isExpanded = useSidebarStore((s) => s.expandedMenuKeys.includes(expandKey));
  const toggleExpandedMenuKey = useSidebarStore((s) => s.toggleExpandedMenuKey);

  // 클릭으로 펼침/접힘을 토글하는 항목: API로 채우는 lazy 메뉴이거나, 정적 children을 토글만 하는 expandable 메뉴
  const isExpandable = !!(menuItem.lazyExpandChildren || menuItem.expandable);
  const childrenList = menuItem.children;
  const hasChildren = !!childrenList?.length;
  const showChevron = isExpandable || hasChildren;

  const isActive = isItemActive(menuItem, pathname);

  // 손자 이하가 활성인 경우에도 조상 행을 활성 표시해야 하므로 재귀로 탐색
  const isChildActive = hasActiveDescendant(childrenList, pathname) || matchesActivePathPrefix(menuItem, pathname);

  /** 클릭해도 아무 일이 없는 행 (경로 미정 + 펼칠 것도 없음) */
  const isInert = !menuItem.path && !isExpandable;

  const handleClick = () => {
    if (isExpandable) {
      toggleExpandedMenuKey(expandKey);
      return;
    }
    if (menuItem.path) navigate(menuItem.path);
  };

  // 자식이 실제로 있을 때만 패널을 연다 — children이 빈 배열이면 내용 없이 들여쓰기 선만 남는다
  const showChildPanel = isSidebarWide && hasChildren && (!isExpandable || isExpanded);

  /**
   * 하위 메뉴 패널. 들여쓰기 규칙:
   * - 폭을 지정하지 않는다. w-full(=100%)이면 ml만큼 박스 전체가 밀려 우측 chevron까지 따라 밀린다.
   *   부모(flex-col)의 stretch에 맡기면 margin만큼 좌측만 줄고 우측 끝은 고정된다.
   * - 1차는 크게, 2차부터는 작게. 깊어질수록 같은 값이 누적되면 과하게 밀린다.
   */
  const childrenPanel = showChildPanel && childrenList && (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      exit={{ height: 0 }}
      transition={TRANSITION.layout}
      className={cn('flex flex-col overflow-hidden', depth === 0 ? 'ml-4' : 'border-table-line ml-4 border-l')}>
      {childrenList.map((childMenuItem) => (
        <SidebarNavItem
          key={childMenuItem.id}
          menuItem={childMenuItem}
          pathname={pathname}
          navigate={navigate}
          depth={depth + 1}
          parentKey={expandKey}
          className='border-table-line border-l'
        />
      ))}
    </motion.div>
  );

  return (
    <>
      <motion.button
        type='button'
        aria-current={isActive ? 'page' : undefined}
        aria-expanded={isExpandable ? isExpanded : undefined}
        aria-disabled={isInert || undefined}
        className={cn(
          'bg-basic-white flex h-13 cursor-default items-center transition',
          !isChildItem && 'font-bold',
          !isInert && 'cursor-pointer hover:brightness-95',
          // 펼친 상태의 활성 표시 — 조상 행까지 색을 주어 활성 경로가 위에서 아래로 이어져 보이게 한다
          isSidebarWide && (isActive || isChildActive) && 'border-blue-600! text-blue-600',
          // 배경은 경로를 실제로 소유한 행만 받는다. 단 자식 패널이 닫혀 있으면 활성 자손이 화면에 없으므로 조상 행이 대신 받는다
          isSidebarWide && (isActive || (isChildActive && !showChildPanel)) && 'bg-support-blue-shade',
          isSidebarWide && 'ml-4',
          menuItem.className,
          className
        )}
        onClick={handleClick}>
        {/* 아이콘 컬럼 — 최상위 항목만 폭을 고정해 접힘 상태에서 아이콘이 레일 정중앙에 오게 한다.
            하위 항목은 들여쓰기 라인이 그 역할을 하므로 폭을 잡지 않는다.
            (width:'auto'를 animate 값으로 주면 motion이 매 렌더마다 실제 폭을 측정하므로 아예 전달하지 않는다)
            아이콘이 없는 하위 항목은 컬럼 자체를 그리지 않는다 — 빈 박스가 남아 텍스트 앞에 어색한 공백이 생기는 것을 막는다.
            최상위 항목은 아이콘이 없어도 접힘 상태 캡션 표시를 위해 컬럼은 유지하되, 펼침 상태에서는 폭을 0으로 접어 라벨 쪽 마진에 자리를 넘긴다. */}
        {(hasIcon || !isChildItem) && (
          <motion.div
            className='relative flex h-full shrink-0 items-center justify-center'
            initial={false}
            {...(!isChildItem && {
              animate: { width: isSidebarWide ? (hasIcon ? ICON_COLUMN_WIDTH.expanded : '0rem') : ICON_COLUMN_WIDTH.collapsed },
              transition: TRANSITION.layout,
            })}>
            {hasIcon && (
              <motion.div
                className={cn('rounded-md p-1', !isSidebarWide && (isActive || isChildActive) && 'bg-support-blue-shade')}
                initial={{ y: -5 }}
                animate={{ y: isSidebarWide ? 0 : -5 }}
                transition={TRANSITION.layout}>
                <i
                  className={cn(
                    'tw-icon-wrap h-6 w-6 text-gray-900',
                    isChildItem && 'text-gray-700',
                    // 접힘·펼침 모두 — 라벨만 파랗고 아이콘은 회색으로 남는 어긋남을 막는다
                    (isActive || isChildActive) && 'text-blue-600!'
                  )}>
                  {menuItem.Icon}
                </i>
              </motion.div>
            )}

            {!isChildItem && (
              <motion.span
                initial={{ opacity: 1 }}
                animate={{ opacity: isSidebarWide ? 0 : 1 }}
                transition={TRANSITION.fade}
                className={cn(
                  'tw-text-ell pointer-events-none absolute bottom-0 w-full text-xs whitespace-nowrap',
                  isActive || isChildActive ? 'text-blue-600' : 'text-gray-900'
                )}>
                {menuItem?.label ?? menuItem.title}
              </motion.span>
            )}
          </motion.div>
        )}

        {/* 확장 시 제목 — 왼쪽에서 슬라이드인 */}
        <motion.span
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: isSidebarWide ? 1 : 0, x: isSidebarWide ? 0 : -8 }}
          transition={TRANSITION.fade}
          className={cn('tw-text-ell min-w-0 flex-1 text-left', !hasIcon && 'ml-3')}>
          {menuItem.title}
        </motion.span>

        {/* 확장 시 chevron */}
        {showChevron && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: isSidebarWide ? 1 : 0 }}
            transition={TRANSITION.fade}
            className={cn('mr-4 shrink-0 transition-transform', isExpandable && isExpanded && 'rotate-180')}>
            <ChevronDownIcon />
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>{childrenPanel}</AnimatePresence>
    </>
  );
});

export default SidebarNavItem;
